import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./BiologyAndCkdSection.css";

const initialColumns = [
  { id: "m6", labelKey: "m6" },
  { id: "m3", labelKey: "m3" },
  { id: "current", labelKey: "current" },
];

const initialRows = [
  { id: "creatinine", nameKey: "creatinine", unit: "µmol/L", target: "-" },
  { id: "egfr", nameKey: "egfrName", unit: "ml/min/1.73m²", target: "≥60" },
  { id: "acr", nameKey: "acr", unit: "mg/g", target: "<30" },
  { id: "hemoglobin", nameKey: "hemoglobin", unit: "g/dL", target: "10–12" },
  { id: "sodiumPotassium", nameKey: "sodiumPotassium", unit: "mmol/L", target: "140 / 3.5–5" },
  { id: "bicarbonates", nameKey: "bicarbonates", unit: "mmol/L", target: "24" },
  { id: "calciumPhosphate", nameKey: "calciumPhosphate", unit: "mmol/L", target: ">2.2 / <1.8" },
  { id: "vitaminD", nameKey: "vitaminD", unit: "ng/mL", target: "30" },
  { id: "pth", nameKey: "pth", unit: "pg/mL", target: "2–9×n1" },
];

const otherParameters = [
  { id: "proteinuria24", unit: "g/24 h" },
  { id: "culture", unit: "" },
  { id: "ferritin", unit: "µg/L" },
  { id: "serumIron", unit: "µmol/L" },
  { id: "transferrin", unit: "%" },
];

const getTrend = (values) => {
  const numericValues = values
    .map((value) => Number.parseFloat(String(value).replace(",", ".")))
    .filter(Number.isFinite);
  if (numericValues.length < 2) return "flat";
  const first = numericValues[0];
  const last = numericValues[numericValues.length - 1];
  if (last > first) return "up";
  if (last < first) return "down";
  return "flat";
};

const getSparklinePoints = (values) => {
  const numericValues = values.map((value) => {
    const number = Number.parseFloat(String(value).replace(",", "."));
    return Number.isFinite(number) ? number : null;
  });
  const validValues = numericValues.filter((value) => value !== null);
  if (validValues.length < 2) return "";
  const min = Math.min(...validValues);
  const max = Math.max(...validValues);
  const range = max - min;

  return numericValues
    .map((value, index) => {
      if (value === null) return null;
      const x = 2 + (index / Math.max(values.length - 1, 1)) * 24;
      const y = range === 0 ? 9 : 16 - ((value - min) / range) * 12;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .filter(Boolean)
    .join(" ");
};

const savedValue = (parameter) =>
  Array.isArray(parameter?.values)
    ? parameter.values
    : [parameter?.m6, parameter?.m3, parameter?.current].map((value) => value ?? "");

const parseNumber = (value) => {
  const number = Number.parseFloat(String(value ?? "").replace(",", "."));
  return Number.isFinite(number) ? number : null;
};

export default function BiologyAndCkdSection({ patientId, biologicalRecord, biologicalLoading, biologicalError }) {
  const { t } = useTranslation("neurologue");
  const translateRef = useRef(t);
  const [stage, setStage] = useState("");
  const [albuminuria, setAlbuminuria] = useState("");
  const [columns, setColumns] = useState(initialColumns);
  const [rows, setRows] = useState(() =>
    initialRows.map((row) => ({ ...row, values: ["", "", ""] })),
  );
  const [otherValues, setOtherValues] = useState({});
  const [showTrends, setShowTrends] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [esaEnabled, setEsaEnabled] = useState(false);
  const [esaDate, setEsaDate] = useState("");
  const fileInputRef = useRef(null);
  translateRef.current = t;

  useEffect(() => {
    const parameters = biologicalRecord?.biologicalParameters || [];
    const findParameter = (id, translatedName) =>
      parameters.find((item) => item.parameter === id)
      || parameters.find((item) => item.parameter === translatedName);
    const legacyVitaminPthValues = savedValue(
      findParameter("vitaminPth", translateRef.current("biology.vitaminPth")),
    );
    const stageParameter = parameters.find((item) => item.parameter === "ckdStage");
    const albuminuriaParameter = parameters.find((item) => item.parameter === "albuminuriaCategory");
    const firstWithPeriods = parameters.find((item) =>
      Array.isArray(item.values) && item.values.length > initialColumns.length,
    );
    const restoredColumns = firstWithPeriods
      ? (Array.isArray(firstWithPeriods.periods) && firstWithPeriods.periods.length
        ? firstWithPeriods.periods
        : Array.from({ length: firstWithPeriods.values.length }, (_, index) =>
            index === firstWithPeriods.values.length - 1
              ? "current"
              : index < initialColumns.length - 1
                ? initialColumns[index].id
                : `m${(index + 1) * 3}`,
          )
      ).map((id, index) => {
          const fixed = initialColumns.find((column) => column.id === id);
          if (fixed) return fixed;
          const month = String(id).match(/^m(\d+)$/)?.[1];
          const legacyMonth = Math.max(9, (index + 1) * 3);
          return {
            id: month ? `m${month}` : `m${legacyMonth}`,
            label: translateRef.current("biology.monthResult", {
              count: Number(month || legacyMonth),
            }),
          };
        })
      : initialColumns;
    const nextColumns = restoredColumns.length
      ? restoredColumns
      : initialColumns;

    setColumns(nextColumns);
    setRows(initialRows.map((row) => {
      const saved = findParameter(row.id, translateRef.current(`biology.${row.nameKey}`));
      const values = saved
        ? savedValue(saved)
        : row.id === "vitaminD" || row.id === "pth"
          ? legacyVitaminPthValues.map((value) =>
              String(value ?? "").split("/")[row.id === "vitaminD" ? 0 : 1]?.trim() || "")
          : [];
      return {
        ...row,
        values: nextColumns.map((_, index) => values[index] ?? ""),
      };
    }));
    setStage(stageParameter?.current || "");
    setAlbuminuria(albuminuriaParameter?.current || "");
    setOtherValues(Object.fromEntries(otherParameters.map(({ id }) => [
      id,
      findParameter(id, translateRef.current(`biology.${id}`))?.current || "",
    ])));
    setEsaEnabled(Boolean(biologicalRecord?.epoEsa));
    setEsaDate(String(biologicalRecord?.epoEsaDate || "").slice(0, 10));
  }, [patientId, biologicalRecord]);

  const addColumn = () => {
    const monthNumbers = columns
      .map((column) => Number(String(column.id).match(/^m(\d+)$/)?.[1]))
      .filter(Number.isFinite);
    const month = Math.max(6, ...monthNumbers) + 3;
    const id = `m${month}`;
    setColumns((current) => [
      ...current.slice(0, -1),
      { id, label: t("biology.monthResult", { count: month }) },
      current[current.length - 1],
    ]);
    setRows((current) => current.map((row) => ({
      ...row,
      values: [...row.values.slice(0, -1), "", row.values[row.values.length - 1]],
    })));
  };

  const updateResult = (rowId, columnIndex, value) => {
    setRows((current) => current.map((row) => row.id === rowId
      ? { ...row, values: row.values.map((result, index) => index === columnIndex ? value : result) }
      : row));
  };

  const getCurrentValue = (id) => rows.find((row) => row.id === id)?.values.at(-1) || "";
  const potassiumValue = getCurrentValue("sodiumPotassium").split("/").at(-1);
  const potassium = parseNumber(potassiumValue);
  const egfr = parseNumber(getCurrentValue("egfr"));
  const hemoglobin = parseNumber(getCurrentValue("hemoglobin"));

  return (
    <section className="neurologue-section-card biology-ckd">
      {biologicalLoading && <p role="status">{t("common.loading", { defaultValue: "Loading…" })}</p>}
      {biologicalError && <p role="alert">{biologicalError}</p>}
      <div className="biology-ckd__title-row">
        <h3>{t("biology.title")}</h3>
        <span className="biology-ckd__hero">{t("biology.hero")}</span>
      </div>

      <div className="biology-ckd__toolbar">
        <div className="biology-ckd__tools">
          <button type="button" onClick={() => setShowTrends((visible) => !visible)}>{showTrends ? t("biology.hideTrends") : t("biology.showTrends")}</button>
          <button type="button" onClick={() => fileInputRef.current?.click()}>{t("biology.import")}</button>
          <input ref={fileInputRef} className="biology-ckd__file-input" type="file" accept=".csv,.xlsx,.xls" onChange={(event) => setSelectedFile(event.target.files?.[0]?.name || "")} />
        </div>
        <label className="biology-ckd__stage">
          {t("biology.stage")}
          <select value={stage} onChange={(event) => setStage(event.target.value)}>
            <option value="">—</option>
            {["G1", "G2", "G3a", "G3b", "G4", "G5"].map((value) => <option key={value}>{value}</option>)}
          </select>
          <strong>{t("biology.egfr")}</strong>
          <select
            aria-label={t("biology.albuminuria")}
            value={albuminuria}
            onChange={(event) => setAlbuminuria(event.target.value)}
          >
            <option value="">—</option>
            {["A1", "A2", "A3"].map((value) => <option key={value}>{value}</option>)}
          </select>
        </label>
      </div>

      {selectedFile && <div className="biology-ckd__imported-file">{t("biology.selectedFile", { name: selectedFile })}</div>}

      {showTrends && (
        <div className="biology-ckd__trend-panel" aria-label={t("biology.trendAria")}>
          {rows.slice(0, 4).map((row) => (
            <div className="biology-ckd__trend" key={row.id}>
              <span>{t(`biology.${row.nameKey}`)}</span>
              <div>{row.values.map((value, index) => {
                const height = Math.min(30, 10 + (parseNumber(value) || 0));
                return <i key={`${row.id}-${index}`} style={{ height: `${height}px` }} title={value} />;
              })}</div>
            </div>
          ))}
        </div>
      )}

      {(potassium !== null && potassium > 5.5
        || egfr !== null && egfr < 30
        || hemoglobin !== null && hemoglobin < 10) && (
        <div className="biology-ckd__alerts" aria-label={t("biology.trendAria")}>
          {potassium !== null && potassium > 5.5 && <div className="biology-ckd__alert biology-ckd__alert--danger">{t("biology.hyperkalemia", { value: potassiumValue })}</div>}
          {egfr !== null && egfr < 30 && <div className="biology-ckd__alert biology-ckd__alert--danger">{t("biology.rrtAlert", { value: egfr })}</div>}
          {hemoglobin !== null && hemoglobin < 10 && <div className="biology-ckd__alert biology-ckd__alert--warning">{t("biology.anemiaAlert", { value: getCurrentValue("hemoglobin") })}</div>}
        </div>
      )}

      <div className="biology-ckd__table-wrap">
        <table className="biology-ckd__table">
          <thead>
            <tr>
              <th>{t("biology.parameter")}</th>
              {columns.filter((column) => column.id !== "current").map((column) => <th data-period-id={column.id} key={column.id}>{column.label || t(`biology.${column.labelKey}`)}</th>)}
              <th className="biology-ckd__add-period-cell">
                <button
                  type="button"
                  className="biology-ckd__add-period"
                  onClick={addColumn}
                  aria-label={t("biology.addColumn")}
                  title={t("biology.addColumn")}
                >
                  <Plus size={16} aria-hidden="true" />
                </button>
              </th>
              {columns.filter((column) => column.id === "current").map((column) => <th data-period-id={column.id} className="is-current" key={column.id}>{column.label || t(`biology.${column.labelKey}`)}</th>)}
              <th>{t("biology.target")}</th>
              <th>{t("biology.trend")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} data-biology-parameter={row.id}>
                <td><strong>{t(`biology.${row.nameKey}`)}</strong><small>{row.unit}</small></td>
                {row.values.map((value, index) => columns[index]?.id !== "current" && (
                  <td data-period-id={columns[index]?.id} className={columns[index]?.id === "current" ? "is-current" : ""} key={`${row.id}-${columns[index]?.id || index}`}>
                    <input aria-label={t("biology.resultAria", { name: t(`biology.${row.nameKey}`), column: columns[index]?.label || t(`biology.${columns[index]?.labelKey || "result"}`) })} value={value} onChange={(event) => updateResult(row.id, index, event.target.value)} />
                  </td>
                ))}
                <td className="biology-ckd__add-period-cell" aria-hidden="true" />
                {row.values.map((value, index) => columns[index]?.id === "current" && (
                  <td data-period-id={columns[index]?.id} className="is-current" key={`${row.id}-${columns[index]?.id}`}>
                    <input aria-label={t("biology.resultAria", { name: t(`biology.${row.nameKey}`), column: t("biology.current") })} value={value} onChange={(event) => updateResult(row.id, index, event.target.value)} />
                  </td>
                ))}
                <td className="biology-ckd__target">{row.target}</td>
                <td>
                  {(() => {
                    const trend = getTrend(row.values);
                    return (
                      <svg className={`biology-ckd__sparkline biology-ckd__sparkline--${trend}`} viewBox="0 0 28 18" role="img" aria-label={`${trend} trend`}>
                        <polyline points={getSparklinePoints(row.values)} />
                      </svg>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="biology-ckd__other">
        <h4>{t("biology.otherBiology")}</h4>
        <div className="biology-ckd__other-grid">
          <label className="biology-ckd__other-field" data-biology-parameter="proteinuria24">
            <span>{t("biology.proteinuria24")}</span>
            <span className="biology-ckd__unit-input"><input type="number" value={otherValues.proteinuria24 || ""} onChange={(event) => setOtherValues((values) => ({ ...values, proteinuria24: event.target.value }))} step="0.1" /><i>{t("biology.g24h")}</i></span>
            <small className="biology-ckd__note">{t("biology.significantProteinuria")}</small>
          </label>
          <label className="biology-ckd__other-field biology-ckd__culture" data-biology-parameter="culture">
            <span>{t("biology.culture")}</span>
            <input type="text" placeholder={t("biology.culturePlaceholder")} value={otherValues.culture || ""} onChange={(event) => setOtherValues((values) => ({ ...values, culture: event.target.value }))} />
          </label>
        </div>
        <h4 className="biology-ckd__iron-title">{t("biology.ironProfile")}</h4>
        <div className="biology-ckd__iron-grid">
          <label className="biology-ckd__other-field biology-ckd__abnormal" data-biology-parameter="ferritin">
            <span>{t("biology.ferritin")}</span>
            <span className="biology-ckd__unit-input"><input type="number" value={otherValues.ferritin || ""} onChange={(event) => setOtherValues((values) => ({ ...values, ferritin: event.target.value }))} /><i>{t("biology.ugL")}</i></span>
            <small className="biology-ckd__note">{t("biology.ironDeficient")}</small>
          </label>
          <label className="biology-ckd__other-field" data-biology-parameter="serumIron">
            <span>{t("biology.serumIron")}</span>
            <span className="biology-ckd__unit-input"><input type="number" value={otherValues.serumIron || ""} onChange={(event) => setOtherValues((values) => ({ ...values, serumIron: event.target.value }))} step="0.1" /><i>{t("biology.umolL")}</i></span>
          </label>
          <label className="biology-ckd__other-field biology-ckd__abnormal" data-biology-parameter="transferrin">
            <span>{t("biology.transferrin")}</span>
            <span className="biology-ckd__unit-input"><input type="number" value={otherValues.transferrin || ""} onChange={(event) => setOtherValues((values) => ({ ...values, transferrin: event.target.value }))} /><i>%</i></span>
            <small className="biology-ckd__note">{t("biology.functionalIronDeficiency")}</small>
          </label>
        </div>
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={esaEnabled}
        data-loaded-checked={String(Boolean(biologicalRecord?.epoEsa))}
        aria-label={t("biology.epo")}
        className={`biology-ckd__esa${esaEnabled ? " is-enabled" : ""}`}
        onClick={() => setEsaEnabled((enabled) => !enabled)}
      >
        <span className="biology-ckd__switch"><i /></span>
        <span><strong>{t("biology.epo")}</strong><small>{t("biology.epoDetail")}</small></span>
      </button>
      {esaEnabled && <label className="biology-ckd__esa-date-label">
        {t("biology.epoDate", { defaultValue: "Date de début" })}
        <input className="biology-ckd__esa-date" type="date" value={esaDate} onChange={(event) => setEsaDate(event.target.value)} />
      </label>}
    </section>
  );
}
